/** Shared invoice breakdown helpers for subscription bills (employees / patients / both). */

export function toMoneyNumber(val) {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  const n = parseFloat(String(val));
  return Number.isFinite(n) ? n : 0;
}

export function normalizeChargeMode(value) {
  if (value === "PATIENTS" || value === "BOTH" || value === "EMPLOYEES") {
    return value;
  }
  return "EMPLOYEES";
}

export function chargesEmployees(mode) {
  return mode === "EMPLOYEES" || mode === "BOTH";
}

export function chargesPatients(mode) {
  return mode === "PATIENTS" || mode === "BOTH";
}

export const INVOICE_SECTION_TITLES = {
  shared: "Subscription",
  employees: "Employees",
  patients: "Patients",
  setup: "Setup",
};

const SECTION_ORDER = ["shared", "employees", "patients", "setup"];

/**
 * @param {object} bill
 */
export function getInvoiceBreakdown(bill) {
  const usageSummary = bill?.usageSummary || {};
  const planSnapshot = bill?.planSnapshot || {};
  const chargeMode = normalizeChargeMode(
    usageSummary.chargeMode ||
      planSnapshot.billingChargeMode ||
      bill?.billingChargeMode,
  );

  const includedUsers =
    usageSummary.includedUsers ?? planSnapshot.includedUsers ?? 0;
  const extraUsers = usageSummary.extraUsers ?? 0;
  const actualUsers = usageSummary.actualUsers ?? 0;
  const includedPatients =
    usageSummary.includedPatients ?? planSnapshot.includedPatients ?? 0;
  const extraPatients = usageSummary.extraPatients ?? 0;
  const actualPatients = usageSummary.actualPatients ?? 0;

  const catalogEmployee = toMoneyNumber(
    usageSummary.employeeBaseAmount ??
      planSnapshot.baseAmount ??
      bill?.employeeBaseAmount ??
      bill?.baseAmount,
  );
  const catalogPatient = toMoneyNumber(
    usageSummary.patientBaseAmount ??
      planSnapshot.patientBaseAmount ??
      bill?.patientBaseAmount,
  );
  const employeesOn = chargesEmployees(chargeMode);
  const patientsOn = chargesPatients(chargeMode);
  const employeeBaseAmount = employeesOn ? catalogEmployee : 0;
  const patientBaseAmount = patientsOn
    ? catalogPatient > 0
      ? catalogPatient
      : employeesOn
        ? 0
        : catalogEmployee
    : 0;
  const baseAmount = employeeBaseAmount + patientBaseAmount;
  const pricePerExtraUser = toMoneyNumber(
    usageSummary.pricePerExtraUser ??
      planSnapshot.pricePerExtraUser ??
      bill?.pricePerExtraUser,
  );
  const extraUsersAmount = toMoneyNumber(
    usageSummary.extraUsersAmount ?? extraUsers * pricePerExtraUser,
  );
  const pricePerExtraPatient = toMoneyNumber(
    usageSummary.pricePerExtraPatient ??
      planSnapshot.pricePerExtraPatient ??
      bill?.pricePerExtraPatient,
  );
  const extraPatientsAmount = toMoneyNumber(
    usageSummary.extraPatientsAmount ?? extraPatients * pricePerExtraPatient,
  );
  const setupChargeAmount = toMoneyNumber(bill?.setupChargeAmount);

  const apiLines = Array.isArray(usageSummary.invoiceLines)
    ? usageSummary.invoiceLines
    : Array.isArray(bill?.usageBreakdown)
      ? bill.usageBreakdown
      : Array.isArray(bill?.lineItems)
        ? bill.lineItems
        : null;

  const hasLegacyCombinedRow =
    Array.isArray(apiLines) &&
    apiLines.some((line) => line?.key === "base" && line?.section === "shared");

  const invoiceLines =
    apiLines && apiLines.length > 0 && !hasLegacyCombinedRow
      ? apiLines.map(normalizeInvoiceLine)
      : buildFallbackLines({
          chargeMode,
          includedUsers,
          actualUsers,
          extraUsers,
          pricePerExtraUser,
          extraUsersAmount,
          includedPatients,
          actualPatients,
          extraPatients,
          pricePerExtraPatient,
          extraPatientsAmount,
          employeeBaseAmount,
          patientBaseAmount,
          setupChargeAmount,
        });

  const chargeSections =
    Array.isArray(usageSummary.chargeSections) &&
    usageSummary.chargeSections.length > 0 &&
    usageSummary.chargeSections.every((s) => s.baseAmount != null)
      ? usageSummary.chargeSections
      : buildChargeSections({
          chargeMode,
          includedUsers,
          actualUsers,
          extraUsers,
          pricePerExtraUser,
          extraUsersAmount,
          includedPatients,
          actualPatients,
          extraPatients,
          pricePerExtraPatient,
          extraPatientsAmount,
          employeeBaseAmount,
          patientBaseAmount,
        });

  const invoiceSections = groupInvoiceLines(invoiceLines);

  const computedLineSum =
    baseAmount + extraUsersAmount + extraPatientsAmount + setupChargeAmount;

  return {
    usageSummary,
    planSnapshot,
    chargeMode,
    includedUsers,
    extraUsers,
    actualUsers,
    includedPatients,
    extraPatients,
    actualPatients,
    baseAmount,
    employeeBaseAmount,
    patientBaseAmount,
    pricePerExtraUser,
    extraUsersAmount,
    pricePerExtraPatient,
    extraPatientsAmount,
    setupChargeAmount,
    invoiceLines,
    invoiceSections,
    chargeSections,
    computedLineSum,
  };
}

function normalizeInvoiceLine(line) {
  return {
    key: line?.key || line?.name || "line",
    section: line?.section || inferSection(line),
    name: line?.name || "",
    description: line?.description || "",
    quantity: line?.quantity == null ? null : Number(line.quantity),
    rate: line?.rate == null ? null : toMoneyNumber(line.rate),
    amount: toMoneyNumber(line?.amount),
  };
}

function inferSection(line) {
  const key = String(line?.key || "");
  if (key === "extra_users" || key === "employee_base") return "employees";
  if (key === "extra_patients" || key === "patient_base") return "patients";
  if (key === "setup") return "setup";
  return "shared";
}

export function isInformationalLine(line) {
  return line?.rate == null && toMoneyNumber(line?.amount) === 0;
}

export function groupInvoiceLines(lines) {
  const bySection = new Map();
  for (const line of lines || []) {
    const section = line.section || inferSection(line);
    const bucket = bySection.get(section) || [];
    bucket.push(line);
    bySection.set(section, bucket);
  }
  return SECTION_ORDER.filter((section) => bySection.has(section)).map(
    (section) => ({
      section,
      title: INVOICE_SECTION_TITLES[section] || section,
      lines: bySection.get(section) || [],
    }),
  );
}

function buildChargeSections({
  chargeMode,
  includedUsers,
  actualUsers,
  extraUsers,
  pricePerExtraUser,
  extraUsersAmount,
  includedPatients,
  actualPatients,
  extraPatients,
  pricePerExtraPatient,
  extraPatientsAmount,
  employeeBaseAmount,
  patientBaseAmount,
}) {
  const sections = [];
  if (chargesEmployees(chargeMode)) {
    sections.push({
      key: "employees",
      title: "Employees",
      included: includedUsers,
      actual: actualUsers,
      extra: extraUsers,
      unitPrice: pricePerExtraUser,
      extraAmount: extraUsersAmount,
      baseAmount: employeeBaseAmount,
      sectionTotal: employeeBaseAmount + extraUsersAmount,
      unitLabel: "employee",
    });
  }
  if (chargesPatients(chargeMode)) {
    sections.push({
      key: "patients",
      title: "Patients",
      included: includedPatients,
      actual: actualPatients,
      extra: extraPatients,
      unitPrice: pricePerExtraPatient,
      extraAmount: extraPatientsAmount,
      baseAmount: patientBaseAmount,
      sectionTotal: patientBaseAmount + extraPatientsAmount,
      unitLabel: "patient",
    });
  }
  return sections;
}

function employeeBasicDescription({ includedUsers, actualUsers, extraUsers }) {
  const extraNote =
    extraUsers > 0
      ? `${extraUsers} extra billed on the next row`
      : "all covered by the basic plan";
  return `Basic plan includes ${includedUsers} employees. ${actualUsers} billable this period (${extraNote}).`;
}

function patientBasicDescription({ includedPatients, actualPatients, extraPatients }) {
  const extraNote =
    extraPatients > 0
      ? `${extraPatients} extra billed on the next row`
      : "all covered by the basic plan";
  return `Basic plan includes ${includedPatients} patients. ${actualPatients} billable this period (${extraNote}).`;
}

function buildFallbackLines({
  chargeMode,
  includedUsers,
  actualUsers,
  extraUsers,
  pricePerExtraUser,
  extraUsersAmount,
  includedPatients,
  actualPatients,
  extraPatients,
  pricePerExtraPatient,
  extraPatientsAmount,
  employeeBaseAmount,
  patientBaseAmount,
  setupChargeAmount,
}) {
  const employeesOn = chargesEmployees(chargeMode);
  const patientsOn = chargesPatients(chargeMode);
  const lines = [];

  if (employeesOn) {
    lines.push({
      key: "employee_base",
      section: "employees",
      name: "Employee basic plan",
      description: employeeBasicDescription({ includedUsers, actualUsers, extraUsers }),
      quantity: 1,
      rate: employeeBaseAmount,
      amount: employeeBaseAmount,
    });
    if (extraUsers > 0) {
      lines.push({
        key: "extra_users",
        section: "employees",
        name: "Extra employees",
        description: `${extraUsers} extra employees × $${pricePerExtraUser.toFixed(2)} each`,
        quantity: extraUsers,
        rate: pricePerExtraUser,
        amount: extraUsersAmount,
      });
    }
  }

  if (patientsOn) {
    lines.push({
      key: "patient_base",
      section: "patients",
      name: "Patient basic plan",
      description: patientBasicDescription({
        includedPatients,
        actualPatients,
        extraPatients,
      }),
      quantity: 1,
      rate: patientBaseAmount,
      amount: patientBaseAmount,
    });
    if (extraPatients > 0) {
      lines.push({
        key: "extra_patients",
        section: "patients",
        name: "Extra patients",
        description: `${extraPatients} extra patients × $${pricePerExtraPatient.toFixed(2)} each`,
        quantity: extraPatients,
        rate: pricePerExtraPatient,
        amount: extraPatientsAmount,
      });
    }
  }

  if (setupChargeAmount > 0) {
    lines.push({
      key: "setup",
      section: "setup",
      name: "One-time setup fee",
      description: "First invoice setup charge",
      quantity: 1,
      rate: setupChargeAmount,
      amount: setupChargeAmount,
    });
  }
  return lines;
}
